import { Phone, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "../lib/site";
import { ZONES } from "../lib/zones";
import { Reveal, MaskedLines } from "../components/Reveal";
import { useSeo } from "../hooks/useSeo";

const groups = [
  { area: "Roma Centro", zones: ["Centro Storico", "Prati", "Trastevere", "Testaccio", "Esquilino", "Monti", "Trieste", "Salario"] },
  { area: "Roma Sud", zones: ["EUR", "Ostiense", "Garbatella", "Tuscolano", "Appio Latino", "Ardeatino", "Laurentino", "Cecchignola"] },
  { area: "Roma Est", zones: ["Tiburtino", "Casilino", "Prenestino", "Centocelle", "Tor Bella Monaca", "Pigneto", "Collatino", "Torre Angela"] },
  { area: "Roma Nord", zones: ["Nomentano", "Montesacro", "Fidene", "Bufalotta", "Talenti", "Conca d'Oro", "Cassia", "Flaminio"] },
  { area: "Roma Ovest", zones: ["Aurelio", "Boccea", "Gianicolense", "Portuense", "Monteverde", "Pisana", "Casalotti", "Primavalle"] },
  { area: "Provincia & Litorale", zones: ["Ostia", "Fiumicino", "Guidonia", "Pomezia", "Ciampino", "Frascati", "Tivoli", "Anzio"] },
];

export default function Zone() {
  useSeo({
    title: "Zone Coperte a Roma | Rottamazione Auto con Ritiro Gratuito",
    description:
      "Rottamazione auto in tutte le zone di Roma e provincia: Centro, EUR, Prati, Trastevere, Tuscolano, Ostia e molte altre. Ritiro gratuito a domicilio h24.",
    path: "/zone",
  });
  return (
    <div>
      <section className="border-b border-[#232323] pt-40 pb-16">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">/ Copertura · {SITE.area}</p>
          <h1 className="mt-4 font-num uppercase leading-[0.85] tracking-tight text-[13vw] md:text-[9vw]">
            <MaskedLines lines={["Zone", "Coperte"]} />
          </h1>
          <p className="mt-6 max-w-xl font-mono text-sm text-[#8a8a8a]">Interveniamo in ogni quartiere di Roma e nei principali comuni della provincia. Non trovi la tua zona? Chiamaci: quasi sempre ci arriviamo.</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 gap-px border border-[#232323] bg-[#232323] md:grid-cols-2 lg:grid-cols-3">
          {groups.map((g, i) => (
            <Reveal key={g.area} delay={(i % 3) * 0.05}>
              <div className="h-full bg-[#0f0f0f] p-8">
                <div className="flex items-center justify-between border-b border-[#232323] pb-4">
                  <h2 className="font-display text-3xl tracking-wide">{g.area}</h2>
                  <span className="font-num text-2xl text-[#232323]">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <ul className="mt-5 grid grid-cols-2 gap-y-2">
                  {g.zones.map((z) => (
                    <li key={z} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.08em] text-[#8a8a8a]">
                      <span className="h-1 w-1 bg-[#ff3300]" /> {z}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-[#232323] bg-[#0f0f0f] py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
              / Pagine dedicate
            </p>
            <h2 className="mt-4 font-display text-5xl md:text-7xl tracking-wide">
              Rottamazione per zona
            </h2>
            <p className="mt-4 max-w-xl font-mono text-sm text-[#8a8a8a]">
              Approfondisci il servizio nella tua area di Roma: ritiro gratuito,
              tempi e quartieri coperti in dettaglio.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-px border border-[#232323] bg-[#232323] sm:grid-cols-2 lg:grid-cols-3">
            {ZONES.map((z, i) => (
              <Reveal key={z.slug} delay={(i % 3) * 0.05}>
                <Link
                  to={`/zone/${z.slug}`}
                  data-testid={`zone-landing-${z.slug}`}
                  className="group flex h-full flex-col justify-between bg-[#0f0f0f] p-7 transition-colors duration-150 hover:bg-[#1a1a1a]"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-num text-2xl text-[#232323]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <ArrowUpRight
                      size={18}
                      className="text-[#8a8a8a] transition-all duration-200 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#ff3300]"
                    />
                  </div>
                  <div className="mt-10">
                    <h3 className="font-display text-3xl tracking-wide group-hover:text-[#ff3300]">
                      {z.name}
                    </h3>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-[#8a8a8a]">
                      Rottamazione auto · {z.tag}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
        <div className="flex flex-col items-start justify-between gap-6 border border-[#232323] bg-[#0f0f0f] p-8 md:flex-row md:items-center">
          <p className="font-display text-3xl md:text-4xl tracking-wide">La tua zona non è in elenco?</p>
          <a href={`tel:${SITE.phoneTel}`} data-testid="zone-call" className="inline-flex items-center gap-3 border border-[#ff3300] bg-[#ff3300] px-6 py-4 font-mono text-xs uppercase tracking-[0.16em] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#ff3300]">
            <Phone size={16} /> Chiama e chiedi
          </a>
        </div>
      </section>
    </div>
  );
}
